
import { Button } from "react-bootstrap";
import { TimerBar } from "components/shared/TimerBar/TimerBar";
import ClockIcon from "assets/Timer.svg";
import { colors, constants } from "utils";
import { SpeechBubble } from "components/shared/SpeechBubble/SpeechBubble";
import { Tooltip } from "components/shared/Tooltip/Tooltip";
import styled from "styled-components";
import { useEffect, useState } from "react";
import { TimerIcon } from "components/shared/Icon/Timer";

// On tablets the card scrolls and would clip the classic speech bubble, so the
// labels ride the portal Tooltip anchored to the icon there; desktop keeps the
// original bubble to the icon's right, shown while hovering the bar.
const useIsTabletWidth = () => {
  const query = constants.TABLET_MEDIA_QUERY;
  const canMatch = typeof window !== "undefined" && typeof window.matchMedia === "function";
  const [isTablet, setIsTablet] = useState(() => canMatch && window.matchMedia(query).matches);

  useEffect(() => {
    if (!canMatch) return;
    const mq = window.matchMedia(query);
    const onChange = (e) => setIsTablet(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [canMatch, query]);

  return isTablet;
};

export const Timer = ({
  timerValue,
  selectedIndex,
  isMoreTimeRequested,
  isShowRequestMoreTimeButton,
  currentTransitionState,
  dispatchMoreTimeRequest
}) => {
  const [displayText, setDisplayText] = useState(false)
  const isTablet = useIsTabletWidth()

  const isSoloTime = currentTransitionState === constants.SOLO_TIME

  const timerAreaBackgroundColor = isSoloTime ? colors.blue500 : colors.pink800

  const moreTimeButton = (
    <MoreTimeButton
      $backgroundColor={timerAreaBackgroundColor}
      disabled={isMoreTimeRequested}
      onClick={dispatchMoreTimeRequest}
      onFocus={() => setDisplayText(true)}
      onBlur={() => setDisplayText(false)}
      aria-label="Get more time"
      aria-describedby={isTablet ? "get-more-time-tip" : undefined}
      type="submit"
    >
      <IconContainer>
        <TimerIcon color={timerAreaBackgroundColor} />
        {!isTablet && (
          <SpeechBubble hide={!displayText} text={"Get More Time"} minWidth={183} positionBottom={25} positionLeft={60} />
        )}
      </IconContainer>
    </MoreTimeButton>
  );

  const almostUpIcon = (
    <TimeWrapper
      role={isTablet ? "img" : undefined}
      aria-label={isTablet ? "Time's almost up" : undefined}
      aria-describedby={isTablet ? "time-almost-up-tip" : undefined}
      tabIndex={isTablet ? 0 : undefined}
    >
      <IconContainer>
        <img src={ClockIcon} alt="time almost up" />
        {!isTablet && (
          <SpeechBubble hide={!displayText} text={"Time's Almost Up!"} minWidth={183} positionBottom={25} positionLeft={60} />
        )}
      </IconContainer>
    </TimeWrapper>
  );

  return timerValue && (
    <Container
      $backgroundColor={timerAreaBackgroundColor}
      onMouseEnter={() => setDisplayText(true)}
      onMouseLeave={() => setDisplayText(false)}
    >
      <TimerBarWrapper>
        <TimerBar duration={timerValue} height="8px" backgroundColor={isSoloTime ? colors.blue950 : colors.pink950} />
      </TimerBarWrapper>
      {isShowRequestMoreTimeButton && !isMoreTimeRequested ? (
        isTablet ? (
          <Tooltip id="get-more-time-tip" tip="Get More Time" placement="top">
            {moreTimeButton}
          </Tooltip>
        ) : moreTimeButton
      ) : (
        isTablet ? (
          <Tooltip id="time-almost-up-tip" tip="Time's Almost Up!" placement="top">
            {almostUpIcon}
          </Tooltip>
        ) : almostUpIcon
      )}
    </Container>
  )
}

const TimeWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const Container = styled.div`
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 40px;
  padding: 8px 16px;
  gap: 4px;
`

const TimerBarWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`

const IconContainer = styled.div`
  position: relative;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  & > img,
  & > svg {
    opacity: 0;
    animation: fadeIn 0.3s ease-in-out forwards;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.8);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

const MoreTimeButton = styled(Button)`
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  border: none;
  border-radius: 20px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;

  &:hover,
  &:focus,
  &:active {
    background-color: ${({ $backgroundColor }) => $backgroundColor};
    opacity: 0.9;
  }
`
